import { type JSX } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { useAtom } from 'ximple';
import { useLocale } from '@repo/i18n';
import { tzdate } from '@repo/tzdate';
import { getNativeLineQuantities } from '@repo/widget-utils/ProductMapper';
import { useMembershipMode } from '@repo/widget-utils/widgetsConfiguration';
import Button from 'src/components/common/timeslots-buttons/button/Button';
import { cartAtom } from 'src/state/cart/cartAtom';
import { checkoutInfoAtom } from 'src/state/checkout-info/checkoutInfoAtom';
import {
    nativeMemberCoverageAtom,
    retryMemberCardCoverage,
    splitMemberCardLine,
} from 'src/state/native-membership/nativeMemberCoverageAtom';
import { findNativeCoverageLine, seedNativeSplitSizes } from './nativeCoverage';
import MemberCardCoveragePill from './MemberCardCoveragePill';
import MemberCardSplitEditor from './MemberCardSplitEditor';
import { useNativeMemberContext } from './NativeMemberContext';

// Per-line member-card selection for the activities "Velg betalingsmetode" step. Reads the shared
// coverage atom (driven by useNativeMemberCoverageEffects) and lets the member steer which card covers
// each line, exclude a line ("pay with money instead"), or split a category across cards. Renders
// nothing for guest/legacy tenants, on a failed preview (shows a retry), or until coverage exists.
export default function NativeMemberCardSelectionPanel(): JSX.Element | null {
    const { t } = useLocale();
    const theme = useTheme();
    const isNative = useMembershipMode() === 'native';
    const { isLoggedIn } = useNativeMemberContext();
    const [{ result, isCalculating, error, excludedLineKeys, lineGroups }] =
        useAtom(nativeMemberCoverageAtom);
    const [cart] = useAtom(cartAtom);
    const [checkoutInfo] = useAtom(checkoutInfoAtom);

    if (!isNative || !isLoggedIn) return null;

    const panelSx = {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(3),
    };

    if (error) {
        return (
            <Box maxWidth="440px" width="100%" sx={panelSx}>
                <Typography variant="h4">{t.your_member_cards}</Typography>
                <Typography role="alert" sx={{ color: theme.palette.error.main }}>
                    {t.member_card_coverage_failed}
                </Typography>
                <Button text={t.retry} onClick={retryMemberCardCoverage} />
            </Box>
        );
    }

    const coverage = result?.member_card;
    if (!coverage || !checkoutInfo) return null;

    const lineQuantities = getNativeLineQuantities(cart, checkoutInfo);
    const lineItemByPriceId = new Map(
        (result?.line_items ?? []).map((li) => [String(li.plan_price_id), li]),
    );

    // A category is steerable if it — or any of its sub-groups — has a card-eligible coverage row, or
    // the member excluded it.
    const controllableKeys = Object.keys(lineQuantities).filter(
        (key) =>
            excludedLineKeys.includes(key) ||
            coverage.per_line.some(
                (line) =>
                    (line.line_key === key || line.line_key.startsWith(`${key}:g`)) &&
                    line.eligible_card_ids.length > 0,
            ),
    );
    if (controllableKeys.length === 0) return null;

    return (
        <Box maxWidth="440px" width="100%" sx={panelSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4">{t.your_member_cards}</Typography>
                {isCalculating && <CircularProgress size={16} />}
            </Box>

            {controllableKeys.map((key) => {
                const quantity = lineQuantities[key];
                const planPriceId = key.slice(key.lastIndexOf(':') + 1);
                const lineItem = lineItemByPriceId.get(planPriceId);
                const when = lineItem?.plan_start
                    ? tzdate(lineItem.plan_start, false).format('D. MMM HH:mm')
                    : '';
                const detail = [
                    when,
                    lineItem ? `${quantity}× ${lineItem.price_category}` : `${quantity}×`,
                ]
                    .filter(Boolean)
                    .join(' · ');
                const sizes = lineGroups[key];
                return (
                    <Box
                        key={key}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing(0.5),
                            paddingBottom: theme.spacing(1),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        {lineItem?.product_name && (
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {lineItem.product_name}
                            </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {detail}
                        </Typography>
                        {sizes && sizes.length > 0 ? (
                            <MemberCardSplitEditor
                                lineKey={key}
                                quantity={quantity}
                                sizes={sizes}
                            />
                        ) : (
                            <MemberCardCoveragePill
                                lineKey={key}
                                quantity={quantity}
                                onSplit={() =>
                                    splitMemberCardLine(
                                        key,
                                        seedNativeSplitSizes(
                                            findNativeCoverageLine(result, key),
                                            quantity,
                                        ),
                                    )
                                }
                            />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
